import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  DialogFooter,
  Paragraph,
} from '@/components/atoms';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/atoms/otp';
import { useEffect, useState } from 'react';

interface SettlementAccountOTPDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (code: string) => void;
  onResend: () => void;
  isConfirming: boolean;
  isResending: boolean;
  accountName: string;
  accountNumber: string;
  bankName: string;
  email?: string | null;
}

export const SettlementAccountOTPDialog = ({
  isOpen,
  onClose,
  onConfirm,
  onResend,
  isConfirming,
  isResending,
  accountName,
  accountNumber,
  bankName,
  email,
}: SettlementAccountOTPDialogProps) => {
  const [code, setCode] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setCode('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (code.length === 5) {
      onConfirm(code);
    }
  };

  const isBusy = isConfirming || isResending;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setCode('');
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <DialogTitle className="text-xl font-bold">
            Confirm bank account change
          </DialogTitle>
          <DialogDescription className="mt-2 text-muted-foreground">
            Send code to confirm change to{' '}
            <span className="font-semibold text-foreground">{accountName}</span>
            {email ? (
              <>
                . A 5-digit code was sent to{' '}
                <span className="font-medium text-foreground">{email}</span>.
              </>
            ) : (
              '. A 5-digit code was sent to your login email.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-border bg-muted/30 px-4 py-3 space-y-1">
          <Paragraph
            size="xs"
            className="text-muted-foreground uppercase tracking-wide font-semibold"
          >
            Account details
          </Paragraph>
          <Paragraph size="sm" className="font-medium text-foreground">
            {accountName}
          </Paragraph>
          <Paragraph size="sm" className="text-muted-foreground">
            {bankName} · {accountNumber}
          </Paragraph>
        </div>

        <div className="flex flex-col items-center justify-center py-2 text-center gap-3">
          <InputOTP
            maxLength={5}
            value={code}
            onChange={setCode}
            disabled={isBusy}
          >
            <InputOTPGroup>
              {Array.from({ length: 5 }).map((_, index) => (
                <InputOTPSlot key={index} index={index} />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <Paragraph size="xs" className="text-muted-foreground">
            Code expires in 5 minutes.
          </Paragraph>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            className="w-full"
            label="Verify & update bank account"
            onClick={handleConfirm}
            disabled={code.length < 5 || isBusy}
            isLoading={isConfirming}
          />
          <Button
            className="w-full"
            variant="outline"
            label="Resend code"
            onClick={() => {
              setCode('');
              onResend();
            }}
            disabled={isBusy}
            isLoading={isResending}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
